import React, {useMemo} from 'react'
import {Platform, StyleSheet, View} from 'react-native'
import {Image} from 'expo-image'
import {
  AppBskyFeedPost,
  type AppBskyEmbedExternal,
  type AppBskyEmbedImages,
  type AppBskyEmbedVideo,
  type AppBskyFeedDefs,
} from '@atproto/api'

import {Text} from '#/components/Typography'
import {parseEmbed} from '#/types/bsky/post'

export type TellusPost = AppBskyFeedDefs.PostView

type Props = {
  post: TellusPost
  onReady?: () => void
  prefetchedAvatarUri?: string
  prefetchedCoverUri?: string
}

const SHARE_CARD_WIDTH = 1080
const SHARE_CARD_HEIGHT = 1920

const CARD_WIDTH = 900
const CARD_PADDING = 64
const AVATAR_SIZE = 88
const BUNNY_WATERMARK = require('../../../assets/watermark-bunny.png')
const AVATAR_FULL_PATH = '/img/avatar/plain/'
const AVATAR_THUMB_PATH = '/img/avatar_thumbnail/plain/'

export function TellusInstagramShareCard({
  post,
  onReady,
  prefetchedAvatarUri,
  prefetchedCoverUri,
}: Props) {
  const {author} = post

  const postText = useMemo(() => {
    if (AppBskyFeedPost.isRecord(post.record)) {
      return post.record.text || ''
    }
    return ''
  }, [post.record])

  const coverImage = useMemo(() => {
    if (prefetchedCoverUri) return prefetchedCoverUri
    if (!post.embed) return undefined

    const parsed = parseEmbed(post.embed)
    const media = parsed.type === 'post_with_media' ? parsed.media : parsed

    if (media.type === 'images') {
      const image = (media.view as AppBskyEmbedImages.View).images[0]
      return image?.thumb || image?.fullsize
    }
    if (media.type === 'video') {
      return (media.view as AppBskyEmbedVideo.View).thumbnail
    }
    if (media.type === 'link') {
      return (media.view as AppBskyEmbedExternal.View).external?.thumb
    }

    return undefined
  }, [post.embed, prefetchedCoverUri])

  const avatarUri = useMemo(() => {
    if (prefetchedAvatarUri) return prefetchedAvatarUri
    if (!author.avatar) return undefined
    if (AVATAR_SIZE < 90) {
      return author.avatar.replace(AVATAR_FULL_PATH, AVATAR_THUMB_PATH)
    }
    return author.avatar
  }, [author.avatar, prefetchedAvatarUri])

  const [avatarReady, setAvatarReady] = React.useState(() => !avatarUri)
  const [coverReady, setCoverReady] = React.useState(() => !coverImage)
  const [layoutReady, setLayoutReady] = React.useState(false)
  const readyCalledRef = React.useRef(false)

  React.useEffect(() => {
    setAvatarReady(!avatarUri)
  }, [avatarUri])

  React.useEffect(() => {
    setCoverReady(!coverImage)
  }, [coverImage])

  React.useEffect(() => {
    readyCalledRef.current = false
    setLayoutReady(false)
  }, [post.uri])

  React.useEffect(() => {
    if (!onReady || readyCalledRef.current) return
    if (!avatarReady || !coverReady || !layoutReady) return
    readyCalledRef.current = true
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        onReady()
      })
    })
  }, [avatarReady, coverReady, layoutReady, onReady])

  const displayName = author.displayName || author.handle
  const handle = author.handle ? `@${author.handle}` : ''

  return (
    <View style={styles.container}>
      <View
        style={styles.card}
        collapsable={false}
        onLayout={() => setLayoutReady(true)}>
        <View style={styles.header}>
          {avatarUri ? (
            <Image
              source={{uri: avatarUri}}
              style={styles.avatar}
              contentFit="cover"
              cachePolicy="memory-disk"
              accessibilityIgnoresInvertColors
              onLoad={() => setAvatarReady(true)}
              onError={() => setAvatarReady(true)}
            />
          ) : (
            <View style={styles.avatarPlaceholder} />
          )}
          <View style={styles.headerText}>
            <Text style={styles.displayName} numberOfLines={1}>
              {displayName}
            </Text>
            <Text style={styles.handle} numberOfLines={1}>
              {handle}
            </Text>
          </View>
        </View>

        {coverImage ? (
          <View style={styles.coverWrapper}>
            <Image
              source={{uri: coverImage}}
              style={styles.coverImage}
              contentFit="cover"
              cachePolicy="memory-disk"
              accessibilityIgnoresInvertColors
              onLoad={() => setCoverReady(true)}
              onError={() => setCoverReady(true)}
            />
          </View>
        ) : null}

        {postText ? (
          <Text style={styles.postText} numberOfLines={4}>
            {postText}
          </Text>
        ) : null}

        <View style={styles.footer}>
          <Text style={styles.footerText}>tellus.app</Text>
        </View>
      </View>

      <View style={styles.watermark}>
        <Image
          source={BUNNY_WATERMARK}
          style={styles.watermarkPng}
          contentFit="contain"
          accessibilityIgnoresInvertColors
        />
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    width: SHARE_CARD_WIDTH,
    height: SHARE_CARD_HEIGHT,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    backgroundColor: '#B00020',
  },
  card: {
    width: CARD_WIDTH,
    backgroundColor: '#ffffff',
    borderRadius: 36,
    padding: CARD_PADDING,
    shadowColor: '#0b1220',
    shadowOpacity: 0.15,
    shadowRadius: 32,
    shadowOffset: {width: 0, height: 12},
    elevation: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 32,
  },
  avatar: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: AVATAR_SIZE / 2,
    backgroundColor: '#e6e6e6',
  },
  avatarPlaceholder: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: AVATAR_SIZE / 2,
    backgroundColor: '#e6e6e6',
  },
  headerText: {
    marginLeft: 24,
    flex: 1,
  },
  displayName: {
    fontSize: 34,
    lineHeight: 48,
    paddingBottom: 4,
    fontWeight: '700',
    color: '#0f172a',
    ...Platform.select({
      android: {
        includeFontPadding: false,
      },
    }),
  },
  handle: {
    fontSize: 26,
    lineHeight: 38,
    paddingBottom: 4,
    color: '#64748b',
    marginTop: 4,
    ...Platform.select({
      android: {
        includeFontPadding: false,
      },
    }),
  },
  coverWrapper: {
    width: '100%',
    aspectRatio: 16 / 9,
    borderRadius: 28,
    overflow: 'hidden',
    marginBottom: 28,
    backgroundColor: '#f1f5f9',
  },
  coverImage: {
    width: '100%',
    height: '100%',
  },
  postText: {
    fontSize: 28,
    lineHeight: 46,
    paddingBottom: 8,
    flexShrink: 0,
    color: '#0f172a',
    marginBottom: 24,
    ...Platform.select({
      android: {
        includeFontPadding: false,
      },
    }),
  },
  footer: {
    marginTop: 8,
  },
  footerText: {
    fontSize: 24,
    color: '#94a3b8',
    fontWeight: '600',
  },
  watermark: {
    position: 'absolute',
    right: 40,
    bottom: 40,
    shadowColor: '#000000',
    shadowOpacity: 0.2,
    shadowRadius: 8,
    shadowOffset: {width: 0, height: 4},
    elevation: 4,
  },
  watermarkPng: {
    width: 72,
    height: 72,
    opacity: 0.8,
  },
})
